import { SkyAvatar, SkyButton, SkyPopover, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { ROLE_LABELS } from "@shared/permissions";
import type { MeInstance } from "@shared/types/auth";
import { Link } from "wouter";
import { BuildVersion } from "@/components/shell/BuildVersion";
import { DisplayTimezonePicker } from "./DisplayTimezonePicker";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useMyWatches } from "@/hooks/use-alert-routing";
import { useMcpEnabled } from "@/hooks/use-mcp";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/lib/auth/AuthContext";
import { ROUTES } from "@/lib/routes";
import { SignOutIcon } from "@/lib/icons";
import { MyAlerts } from "./MyAlerts";
import { SecurityAlertsDrawer, SecurityAlertsEntry } from "./SecurityAlerts";
import { TenantInstancePicker } from "./TenantInstancePicker";
import { ThemeToggle } from "./ThemeToggle";

/** "Ada Lovelace" → "AL"; falls back to the email's first letter. */
function initialsFor(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return (email[0] ?? "?").toUpperCase();
}

/**
 * The scope a role covers (#1288): a Tenant admin covers the whole tenant and a Platform admin
 * every tenant, so neither is "in <instance>". The other roles are held in the instance.
 */
function roleScopeLine(instance: MeInstance): string {
  const label = ROLE_LABELS[instance.roleName];
  if (instance.roleName === "PlatformAdmin") return `${label} across all tenants`;
  if (instance.roleName === "TenantAdmin") return `${label} in ${instance.tenantDisplayName}`;
  return `${label} in ${instance.name}`;
}

/**
 * Identity, the tenant and instance picker, alerts, appearance, the time zone, sign-out and the
 * build, in the top bar's right-hand slot (UX-1), in skyAdvisor's order (owner, round 3,
 * 2026-09-30).
 *
 * The role shown is the one the user holds IN THE SELECTED INSTANCE, not a
 * global role: the same person can be an InstanceAdmin in one instance and a
 * Viewer in another, and showing the wrong one is how someone concludes the app
 * has lost their permissions.
 */
export function TopbarUserProfile() {
  const { account, signOut } = useAuth();
  const { instances, selectedInstance } = useTenantInstance();
  const [open, setOpen] = useState(false);
  // B-48: a platform administrator can never connect an AI app (the consent refuses the role), so
  // their menu does not offer the page; Admin > AI apps is where they administer MCP.
  const { hasPlatformScope } = usePermissions();
  const mcpEnabled = useMcpEnabled() && !hasPlatformScope;
  // Security alerts: the drawer lives outside the menu, so closing the menu does not close it.
  const [securityOpen, setSecurityOpen] = useState(false);
  // B-76 (round 9): My alerts is read as the pointer or the keyboard reaches the trigger, so the
  // list is there when the menu opens and nothing under it moves (a late list pushed Appearance
  // down about 60 px just as someone aimed at it).
  const [warm, setWarm] = useState(false);
  useMyWatches(warm || open);

  if (!account) return null;

  const initials = initialsFor(account.name, account.email);

  return (
    <>
      <SkyPopover
        open={open}
        onOpenChange={setOpen}
        placement="bottom-end"
        // B-03 (round 9): bounded to the viewport under the top bar, the body scrolls and Sign out
        // stays in view as the footer, at any height (a tablet's 500 px included).
        className="flex max-h-(--radix-popover-content-available-height) w-80 flex-col overflow-hidden p-0"
        testId="user-menu"
        trigger={
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-1 py-1 transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            aria-label="Open user menu"
            data-testid="user-menu-trigger"
            onPointerEnter={() => setWarm(true)}
            onFocus={() => setWarm(true)}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
              {initials}
            </span>
          </button>
        }
      >
        <>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="user-menu-body">
            <div className="flex items-start gap-3 border-b border-border p-4">
              <SkyAvatar name={account.name || account.email} size={40} className="shrink-0" />
              <div className="min-w-0 flex-1">
                <SkyText size={300} weight="semibold" block truncate>
                  {account.name}
                </SkyText>
                <SkyText size={200} color="muted" block truncate>
                  {account.email}
                </SkyText>
                {selectedInstance ? (
                  <SkyText size={200} color="muted" block>
                    {roleScopeLine(selectedInstance)}
                  </SkyText>
                ) : null}
              </div>
            </div>

            {/* The tenant and the instance, under the person (owner, round 3, 2026-09-30): the
              skyAdvisor order, Tenant then Instance, before the rest of the menu. Since round 4
              this is the shell's one picker (the top bar's label is plain text), so "All
              instances" moved here from the bar: the way to the full picker page, and a
              PlatformAdmin's way to platform administration, offered even with no instance. */}
            <div className="border-b border-border p-4" data-testid="user-menu-instance">
              {instances.length > 0 ? (
                <TenantInstancePicker className="mb-4" onChosen={() => setOpen(false)} />
              ) : null}
              <Link
                href="/instances"
                data-testid="user-menu-all-instances"
                onClick={() => setOpen(false)}
                className="rounded-sm text-sm font-medium text-accent-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                All instances
              </Link>
            </div>

            {/* AI apps over MCP (ADR 0009): the person's own connections, while MCP is on. */}
            {mcpEnabled ? (
              <div className="border-b border-border px-4 py-3">
                <Link
                  href={ROUTES.connectedApps}
                  data-testid="user-menu-connected-apps"
                  onClick={() => setOpen(false)}
                  className="rounded-sm text-sm font-medium text-accent-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Connected apps
                </Link>
              </div>
            ) : null}

            {/* Alert routing (2026-09-28): the caller's own Watches in this instance; security
            alerts (2026-09-29) for a TenantAdmin or PlatformAdmin, whatever the instance. */}
            <div className="border-b border-border py-1">
              <MyAlerts onOpenPage={() => setOpen(false)} />
              <SecurityAlertsEntry
                onChoose={() => {
                  setOpen(false);
                  setSecurityOpen(true);
                }}
              />
            </div>

            <div className="border-b border-border py-1">
              <SkyText
                as="p"
                size={200}
                color="muted"
                className="px-4 pb-1 pt-1 uppercase tracking-wide"
              >
                Appearance
              </SkyText>
              <ThemeToggle />
            </div>

            {/* Round 3 (2026-09-30): the zone every time on screen is shown in, the person's own. */}
            <div className="px-4 py-3">
              <DisplayTimezonePicker />
            </div>
          </div>

          {/* The logoff control (HIPAA) is the menu's footer, outside the scrolling body. */}
          <div className="shrink-0 border-t border-border" data-testid="user-menu-footer">
            <div className="p-3">
              <SkyButton
                appearance="primary"
                icon={SignOutIcon}
                className="w-full"
                testId="sign-out"
                onClick={() => {
                  setOpen(false);
                  void signOut("user");
                }}
              >
                Sign out
              </SkyButton>
            </div>
            {/* The build, last, as skyAdvisor ends its menu with its version. Its own test id:
              `build-version` stays the footer's, unique on the screen. */}
            <div className="px-4 pb-3">
              <BuildVersion testId="user-menu-build-version" showDetail />
            </div>
          </div>
        </>
      </SkyPopover>
      <SecurityAlertsDrawer open={securityOpen} onClose={() => setSecurityOpen(false)} />
    </>
  );
}
